import { Button } from 'blaze-ui/Button'
import Styles from './StrategiesSelection.module.scss'
import { MultiStepStepLayout } from 'components/MultiStepFlow/MultiStepStepLayout/MultiStepStepLayout'
import { StickyFooterProvider } from 'providers/StickyFooterProvider/StickyFooterProvider'
import { FooterButtons, StickyFooter } from 'components/FirstTimeExperienceModal/components/Footer/Footer'
import { RadioGroup } from 'components/RadioGroup'
import { useEffect, useState } from 'react'
import {
  strategiesDefaultData,
  strategiesDefaultDataV2,
  strategiesToContentStrategies,
  Strategy,
  buildContentStrategyTactics,
  numberWithCommas,
  mergeStrategyDefaults,
} from './utils'
import { Strategy as StrategyComponent } from './Strategy'
import { useUpdateBrandPlan } from 'hooks/queries/useUpdateBrandPlan'
import { BrandPlanTactic, BusinessType, Goal, SelectedBlogPlatform, SelectedEmailPlatform } from 'types'
import { ExternalIntegrationEnum } from 'api/clients/ExternalIntegrationsClient'
import { useBrandPlan } from 'hooks/queries/brandPlans/useBrandPlan'
import dayjs from 'dayjs'
import { useCreditsToGenerateContent } from 'hooks/useNeedsCreditsToGenerateContent'
import { CreditsPopover } from './CreditsPopover'
import { Text } from 'blaze-ui/Text/Text'
import { Heading } from 'blaze-ui/Heading/Heading'
import { ChevronDown } from 'blaze-ui/icons'
import { PaymentPlanKey } from 'api/clients/PaymentPlanClient'
import { observer } from 'mobx-react-lite'
import { TopBar } from '../TopBar/TopBar'
import { useContentDefaults } from 'hooks/queries/useContentDefaults'
import { useRootStore } from 'stores/RootStore'
import { showToastMessage } from 'common/ToastMessage/ShowToastMessage'
import { Info } from 'almanac-ui/icons'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { Credits } from 'blaze-ui/icons/20/Credits'
import { CreditsBreakdownDrawer } from './CreditsBreakdownDrawer'
import { LoadingEllipses } from 'components/Wizards/Wizard/Form/SectionHeader'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { useFlipperFlag } from 'hooks/useFlipperFlag'

export interface StrategiesSelectionProps {
  onNext: (brandPlanStrategies: Strategy[], planKey: PaymentPlanKey) => void
  onBack: () => void
  initialSelectedStrategies?: Strategy[]
  selectedBusinessType: BusinessType
  goal: Goal
  brandPlanId: number
  isSaving?: boolean
  highlightStep?: string
}

export const StrategiesSelection = observer(
  ({
    onNext,
    onBack,
    initialSelectedStrategies,
    selectedBusinessType,
    goal,
    brandPlanId,
    isSaving,
    highlightStep = 'goalsAndPlan',
  }: StrategiesSelectionProps) => {
    const isNewPostContentTypesEnabled = useFlipperFlag('post_content_types')
    const { isMobile } = useResponsiveDesign()
    const [isUpdatingBrandPlan, setIsUpdatingBrandPlan] = useState(false)
    const { data: brandPlan } = useBrandPlan(brandPlanId)
    const { data: contentDefaults } = useContentDefaults()

    const mergedDefaults = mergeStrategyDefaults(
      isNewPostContentTypesEnabled ? strategiesDefaultDataV2 : strategiesDefaultData,
      contentDefaults?.content_strategy_defaults,
    )
    const [strategies, setStrategies] = useState<Strategy[]>(() => {
      if (initialSelectedStrategies) return initialSelectedStrategies
      return mergedDefaults[selectedBusinessType].strategies.map((strategy, index) => ({
        ...strategy,
        isSelected: index === 0,
      }))
    })

    const selectedStrategies = strategies.filter((strategy) => strategy.isSelected)

    const [selectedBlogPlatform, setSelectedBlogPlatform] = useState<SelectedBlogPlatform>(
      ExternalIntegrationEnum.WordPress,
    )
    const [selectedEmailPlatform, setSelectedEmailPlatform] = useState<SelectedEmailPlatform>(
      ExternalIntegrationEnum.Mailchimp,
    )

    const {
      tierUsageStore: { autopilotPlans },
      drawersStore,
    } = useRootStore()

    const starterPlanCreditLimit = Number(
      autopilotPlans.find((plan) => plan.key === PaymentPlanKey.BlazeAutopilotStarter)?.prices[0]?.metadata
        ?.credit_limit ?? 0,
    )

    useEffect(() => {
      if (brandPlan?.selected_blog_platform) {
        setSelectedBlogPlatform(brandPlan.selected_blog_platform)
      }
      if (brandPlan?.selected_email_platform) {
        setSelectedEmailPlatform(brandPlan.selected_email_platform)
      }
    }, [brandPlan?.selected_blog_platform, brandPlan?.selected_email_platform])

    useEffect(() => {
      if (initialSelectedStrategies?.some((strategy) => strategy.isSelected)) return

      const updatedStrategies = mergedDefaults[selectedBusinessType].strategies.map((strategy, index) => ({
        ...strategy,
        isSelected: index === 0,
      }))
      setStrategies(updatedStrategies)
    }, [initialSelectedStrategies, contentDefaults]) // eslint-disable-line react-hooks/exhaustive-deps

    const { mutate: updateBrandPlan } = useUpdateBrandPlan({ id: brandPlanId })

    const handleNext = () => {
      setIsUpdatingBrandPlan(true)
      const selectedStrategiesValues = selectedStrategies.map((strategy) => ({
        ...strategy,
        tactics: buildContentStrategyTactics(strategy),
      }))

      updateBrandPlan(
        {
          content_strategies: strategiesToContentStrategies(selectedStrategiesValues),
          goal: goal,
          business_type: selectedBusinessType,
          start_date: dayjs().add(1, 'day').format('YYYY-MM-DD'),
          post_content_types: !!isNewPostContentTypesEnabled,
          selected_blog_platform: selectedStrategiesValues.some((strategy) =>
            strategy.tactics.includes(BrandPlanTactic.SEO),
          )
            ? selectedBlogPlatform
            : null,
          selected_email_platform: selectedStrategiesValues.some((strategy) =>
            strategy.tactics.includes(BrandPlanTactic.Email),
          )
            ? selectedEmailPlatform
            : null,
        },
        {
          onSuccess: () => {
            setIsUpdatingBrandPlan(false)
            onNext(
              strategies,
              isGrowthPlan ? PaymentPlanKey.BlazeAutopilotGrowth : PaymentPlanKey.BlazeAutopilotStarter,
            )
          },
          onError: () => {
            setIsUpdatingBrandPlan(false)
            showToastMessage(
              'An error occurred during content strategy selection. Try again. Contact support if the issue persists.',
              'error',
            )
          },
        },
      )
    }

    const totalBlogCount = selectedStrategies.reduce((acc, strategy) => acc + strategy.seo_contents[0].count, 0)
    const totalEmailCount = selectedStrategies.reduce((acc, strategy) => acc + strategy.email_contents[0].count, 0)
    const totalShortFormVideoCount = selectedStrategies.reduce(
      (acc, strategy) => acc + strategy.video_crosspost_contents[0].count,
      0,
    )

    const allCrosspostContents = selectedStrategies.flatMap((s) => s.crosspost_contents)
    const stillImageCount = isNewPostContentTypesEnabled
      ? allCrosspostContents.filter((c) => c.content_type === 'still_image').reduce((sum, c) => sum + c.count, 0)
      : 0
    const carouselCount = isNewPostContentTypesEnabled
      ? allCrosspostContents.filter((c) => c.content_type === 'carousel').reduce((sum, c) => sum + c.count, 0)
      : 0
    const feedVideoCount = isNewPostContentTypesEnabled
      ? allCrosspostContents.filter((c) => c.content_type === 'video').reduce((sum, c) => sum + c.count, 0)
      : 0
    const totalSocialCount = isNewPostContentTypesEnabled
      ? 0
      : allCrosspostContents.reduce((sum, c) => sum + c.count, 0)

    const credits = useCreditsToGenerateContent(
      totalSocialCount,
      totalBlogCount,
      totalEmailCount,
      totalShortFormVideoCount,
      stillImageCount,
      feedVideoCount,
      carouselCount,
      0,
      { applyVisualStyle: true, percentageOfVisualStylesGeneration: 1 },
    )
    const monthlyCredits = Math.floor(credits.creditsNeeded * 4.33)
    const isGrowthPlan = monthlyCredits >= starterPlanCreditLimit

    const stillImageCreditsNeeded = stillImageCount * (credits.perTypeCosts?.stillImage ?? 0)
    const carouselCreditsNeeded = carouselCount * (credits.perTypeCosts?.carousel ?? 0)
    const feedVideoCreditsNeeded = feedVideoCount * (credits.perTypeCosts?.feedVideo ?? 0)

    const defaultStrategy = strategies[0]
    const isSavingChanges = isUpdatingBrandPlan || isSaving

    return (
      <MultiStepStepLayout
        title="Here's your recommended content strategy"
        headingLevel={isMobile ? 2 : 1}
        scrollContainerClassName={Styles.scrollContainer}
      >
        <StickyFooterProvider
          footer={
            <StickyFooter className={Styles.footerContainer}>
              <FooterButtons className={Styles.footerButtons}>
                {isMobile ? (
                  <div className={Styles.mobileFooterContent}>
                    <div
                      className={Styles.mobileFooterCredits}
                      onClick={() =>
                        drawersStore.openDrawer(CreditsBreakdownDrawer, {
                          totalSocialCount,
                          totalBlogCount,
                          totalEmailCount,
                          totalShortFormVideoCount,
                          monthlyCredits,
                          isGrowthPlan,
                          blogCountCreditsNeeded: credits.creditsNeededPerContentType?.blog ?? 0,
                          emailCountCreditsNeeded: credits.creditsNeededPerContentType?.email ?? 0,
                          socialCountCreditsNeeded: credits.creditsNeededPerContentType?.social ?? 0,
                          shortFormVideoCountCreditsNeeded: credits.creditsNeededPerContentType?.shortFormVideo ?? 0,
                          stillImageCount,
                          carouselCount,
                          feedVideoCount,
                          stillImageCreditsNeeded,
                          carouselCreditsNeeded,
                          feedVideoCreditsNeeded,
                        })
                      }
                    >
                      <Credits size={20} color="var(--dark-60)" />
                      <Text>
                        Estimated monthly credits: {numberWithCommas(monthlyCredits)}
                        <ChevronDown size={12} />
                      </Text>
                    </div>
                    <Button
                      size="lg"
                      onPress={handleNext}
                      isDisabled={isSavingChanges || selectedStrategies.length === 0}
                      fullWidth
                    >
                      {isSavingChanges ? (
                        <>
                          Saving changes
                          <LoadingEllipses />
                        </>
                      ) : (
                        'Looks good'
                      )}
                    </Button>
                  </div>
                ) : (
                  <>
                    <Button variant="tertiary" size="lg" onPress={onBack}>
                      Back
                    </Button>
                    <div className={Styles.footerRightContent}>
                      <CreditsPopover
                        blogCountCreditsNeeded={credits.creditsNeededPerContentType?.blog ?? 0}
                        emailCountCreditsNeeded={credits.creditsNeededPerContentType?.email ?? 0}
                        socialCountCreditsNeeded={credits.creditsNeededPerContentType?.social ?? 0}
                        shortFormVideoCountCreditsNeeded={credits.creditsNeededPerContentType?.shortFormVideo ?? 0}
                        totalSocialCount={totalSocialCount}
                        totalBlogCount={totalBlogCount}
                        totalEmailCount={totalEmailCount}
                        totalShortFormVideoCount={totalShortFormVideoCount}
                        monthlyCredits={monthlyCredits}
                        isGrowthPlan={isGrowthPlan}
                        stillImageCount={stillImageCount}
                        carouselCount={carouselCount}
                        feedVideoCount={feedVideoCount}
                        stillImageCreditsNeeded={stillImageCreditsNeeded}
                        carouselCreditsNeeded={carouselCreditsNeeded}
                        feedVideoCreditsNeeded={feedVideoCreditsNeeded}
                      >
                        <div className={Styles.footerNote}>
                          <Text variant="label" color="var(--dark-40)">
                            Estimated monthly credits
                          </Text>
                          <Heading level={5}>
                            {numberWithCommas(monthlyCredits)}
                            <ChevronDown size={12} />
                          </Heading>
                        </div>
                      </CreditsPopover>
                      <a href="https://www.blaze.ai/pricing" target="_blank" rel="noopener noreferrer">
                        <div className={Styles.footerNote}>
                          <Text variant="label" color="var(--dark-40)">
                            Recommended Plan
                          </Text>
                          <Heading level={5}>
                            {isGrowthPlan ? 'Growth' : 'Starter'} Plan
                            <div className={Styles.infoIcon}>
                              <Info size={12} />
                            </div>
                          </Heading>
                        </div>
                      </a>
                      <Button
                        size="lg"
                        onPress={handleNext}
                        isDisabled={isSavingChanges || selectedStrategies.length === 0}
                      >
                        {isSavingChanges ? (
                          <>
                            Saving changes
                            <LoadingEllipses />
                          </>
                        ) : (
                          `Continue with ${isGrowthPlan ? 'Growth' : 'Starter'} plan`
                        )}
                      </Button>
                    </div>
                  </>
                )}
              </FooterButtons>
            </StickyFooter>
          }
        >
          <TopBar highlightStep={highlightStep as React.ComponentProps<typeof TopBar>['highlightStep']} />
          <RadioGroup
            value={selectedStrategies[0]?.id ?? ''}
            className={Styles.strategiesContainer}
            aria-label="Strategies"
            onChange={(value) => setStrategies(strategies.map((s) => ({ ...s, isSelected: s.id === value })))}
          >
            <ResponsiveDesign.Desktop>
              <div className={Styles.defaultStrategy}>
                <StrategyComponent
                  key={defaultStrategy.id}
                  strategy={defaultStrategy}
                  onSelect={(strategyId) => {
                    const currentlySelected = strategies.find((s) => s.isSelected)?.id
                    if (currentlySelected === strategyId) {
                      setStrategies(strategies.map((s) => ({ ...s, isSelected: false })))
                    } else {
                      setStrategies(strategies.map((s) => ({ ...s, isSelected: s.id === strategyId })))
                    }
                  }}
                />
              </div>
              <Heading level={4}>
                Other strategies which businesses in your industry do and you can start with as well.
              </Heading>
            </ResponsiveDesign.Desktop>
            {strategies.slice(isMobile ? 0 : 1).map((strategy) => (
              <StrategyComponent
                key={strategy.id}
                strategy={strategy}
                onSelect={(strategyId) => {
                  const currentlySelected = strategies.find((s) => s.isSelected)?.id
                  if (currentlySelected === strategyId) {
                    setStrategies(strategies.map((s) => ({ ...s, isSelected: false })))
                  } else {
                    setStrategies(strategies.map((s) => ({ ...s, isSelected: s.id === strategyId })))
                  }
                }}
              />
            ))}
          </RadioGroup>
        </StickyFooterProvider>
      </MultiStepStepLayout>
    )
  },
)
